import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiMenu, FiX, FiHeart, FiUsers, FiBookOpen, FiDownload, FiCheckCircle } = FiIcons;

function Layout({ children }) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  const navigation = [
    { name: 'Hem', path: '/', icon: FiHeart },
    { name: 'Om Okej-kulturen', path: '/om', icon: FiUsers },
    { name: 'Principer', path: '/principer', icon: FiBookOpen },
    { name: 'Resurser', path: '/resurser', icon: FiDownload },
    { name: 'Utvärdering', path: '/assessment', icon: FiCheckCircle }
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-blue-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <Link to="/" className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-green-500 rounded-full flex items-center justify-center">
                <SafeIcon icon={FiHeart} className="text-white text-xl" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-800">Okej-kulturen</h1>
                <p className="text-xs text-gray-600">Den humana arbetsplatsen</p>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex space-x-6">
              {navigation.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-colors ${
                    isActive(item.path)
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-600 hover:text-blue-600 hover:bg-blue-50'
                  }`}
                >
                  <SafeIcon icon={item.icon} className="text-sm" />
                  <span className="font-medium text-sm">{item.name}</span>
                </Link>
              ))}
            </nav>

            {/* Mobile menu button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="lg:hidden p-2 rounded-lg text-gray-600 hover:text-blue-600 hover:bg-blue-50"
            >
              <SafeIcon icon={isMenuOpen ? FiX : FiMenu} className="text-xl" />
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="lg:hidden border-t border-blue-100 bg-white/95 backdrop-blur-sm"
            >
              <div className="px-4 py-2 space-y-1">
                {navigation.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setIsMenuOpen(false)}
                    className={`flex items-center space-x-3 px-3 py-3 rounded-lg transition-colors ${
                      isActive(item.path)
                        ? 'bg-blue-100 text-blue-700'
                        : 'text-gray-600 hover:text-blue-600 hover:bg-blue-50'
                    }`}
                  >
                    <SafeIcon icon={item.icon} className="text-lg" />
                    <span className="font-medium">{item.name}</span>
                  </Link>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* Main Content */}
      <main>
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-green-500 rounded-full flex items-center justify-center">
                  <SafeIcon icon={FiHeart} className="text-white text-sm" />
                </div>
                <span className="text-lg font-bold">Okej-kulturen</span>
              </div>
              <p className="text-gray-400 text-sm">
                En guide till den moderna, humana arbetsplatsen där det är okej att vara människa.
              </p>
            </div>
            
            <div>
              <h3 className="font-semibold mb-4">Huvudsidor</h3>
              <ul className="space-y-2">
                {navigation.slice(0, 3).map((item) => (
                  <li key={item.path}>
                    <Link to={item.path} className="text-gray-400 hover:text-white text-sm transition-colors">
                      {item.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            
            <div>
              <h3 className="font-semibold mb-4">Resurser</h3>
              <ul className="space-y-2">
                <li>
                  
                </li>
                <li>
                  
                </li>
                <li>
                  <Link to="/forskning" className="text-gray-400 hover:text-white text-sm transition-colors">
                    Forskning & Bakgrund
                  </Link>
                </li>
                <li>
                  <Link to="/framgang" className="text-gray-400 hover:text-white text-sm transition-colors">
                    Framgångshistorier
                  </Link>
                </li>
               
                <li>
                  <Link to="/resurser" className="text-gray-400 hover:text-white text-sm transition-colors">
                    Ladda ner
                  </Link>
                </li>

              </ul>
            </div>
            
            <div>
              <h3 className="font-semibold mb-4">Kontakt</h3>
              <p className="text-gray-400 text-sm mb-2">Jonas Jaani</p>
              <a 
                href="https://www.jonasjaani.se" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-400 hover:text-blue-300 text-sm transition-colors"
              >
                www.jonasjaani.se
              </a>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-8 pt-8 text-center">
            <p style={{
      color: 'rgb(156, 163, 175)'
    }} className="text-gray-400 text-sm">© 2025 Jonas Jaani. arbetsplats.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default Layout;